// src/config/rbacConfig.js
// Single source of truth for all RBAC definitions.
// When the backend is ready, permission enforcement moves to DRF; this file
// drives UI visibility only (sidebar sections, action buttons).

// ── Role Definitions ──────────────────────────────────────────────────────────
// Mirrors the backend DRF permission class names exactly.
export const ROLES = {
  R01: {
    id: 'R01',
    label: 'Administrator',
    backendClass: 'nlims_system_administrator',
    institution: 'DPRS / Natview',
    geographicScope: 'National — unrestricted',
    primaryFunction: 'System config, user management, audit logs, integration endpoints, reference data',
  },
  R02: {
    id: 'R02',
    label: 'National Data Manager',
    backendClass: 'national_data_steward',
    institution: 'DPRS — FMLD',
    geographicScope: 'National — all states, all modules',
    primaryFunction: 'National stewardship and cross-module coordination',
  },
  R03: {
    id: 'R03',
    label: 'Federal Department Officer',
    backendClass: 'domain_data_steward',
    institution: 'FMLD Technical Depts',
    geographicScope: 'National — assigned module(s) only',
    primaryFunction: 'Domain-specific stewardship for assigned modules',
  },
  R04: {
    id: 'R04',
    label: 'State Officer',
    backendClass: 'sub_national_validator',
    institution: 'State Ministry / SLDA',
    geographicScope: 'Own state only — all modules',
    primaryFunction: 'State-level validation and administration',
  },
  R05: {
    id: 'R05',
    label: 'Field Officer',
    backendClass: 'data_originator',
    institution: 'LGA Dept / Field staff',
    geographicScope: 'Own LGA only',
    primaryFunction: 'Field data capture and local record maintenance',
  },
  R06: {
    id: 'R06',
    label: 'External Viewer',
    backendClass: 'analytics_consumer',
    institution: 'Research / Dev partners',
    geographicScope: 'National - all states, approved aggregates only',
    primaryFunction: 'Read-only analytics and reporting on approved datasets',
  },
  R07: {
    id: 'R07',
    label: 'Integration System',
    backendClass: 'external_partner_restricted',
    institution: 'NAITS / NADIS / NAIC / CBN / NIRSAL / BOA',
    geographicScope: 'Single assigned API endpoint only',
    primaryFunction: 'Machine-to-machine integration only',
    uiAccess: false, // API-only — no UI login
  },
};

function normalizeRoleLookupText(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

const ROLE_ALIAS_MAP = {
  admin: 'R01',
  administrator: 'R01',
  systemadmin: 'R01',
  systemadministrator: 'R01',
  nlimssystemadministrator: 'R01',
  nationaldatamanager: 'R02',
  nationaldatasteward: 'R02',
  nationalapprover: 'R02',
  federaldepartmentofficer: 'R03',
  departmentofficer: 'R03',
  deptofficer: 'R03',
  domaindatasteward: 'R03',
  statedataofficer: 'R04',
  stateofficer: 'R04',
  supervisor: 'R04',
  subnationalvalidator: 'R04',
  lgafieldofficer: 'R05',
  lgadataofficer: 'R05',
  lgaofficer: 'R05',
  dataoriginator: 'R05',
  fieldofficer: 'R05',
  externalviewer: 'R06',
  viewer: 'R06',
  analyticsconsumer: 'R06',
  externalpartnerrestricted: 'R07',
  integrationsystem: 'R07',
};

export function resolveRoleIdFromValue(value) {
  if (value && typeof value === 'object') {
    const candidates = [
      value.role_id,
      value.roleId,
      value.id,
      value.code,
      value.role_code,
      value.roleCode,
      value.backend_class,
      value.backendClass,
      value.name,
      value.label,
      value.role_name,
      value.roleName,
      value.role_label,
      value.roleLabel,
    ];

    for (const candidate of candidates) {
      const resolved = resolveRoleIdFromValue(candidate);
      if (resolved) return resolved;
    }

    return null;
  }

  const text = String(value ?? '').trim();
  if (!text) return null;
  if (/^R0[1-7]$/i.test(text)) return text.toUpperCase();

  const normalized = normalizeRoleLookupText(text);
  if (!normalized) return null;

  if (ROLE_ALIAS_MAP[normalized]) return ROLE_ALIAS_MAP[normalized];

  for (const [roleId, role] of Object.entries(ROLES)) {
    const candidates = [role.id, role.label, role.backendClass];
    if (candidates.some((candidate) => normalizeRoleLookupText(candidate) === normalized)) {
      return roleId;
    }
  }

  return null;
}

export function isNationalScopeRole(roleId) {
  return ['R01', 'R02', 'R03', 'R06'].includes(resolveRoleIdFromValue(roleId));
}

export function requiresGeoScopeSelection(roleId) {
  return ['R04', 'R05'].includes(resolveRoleIdFromValue(roleId));
}

// UI-facing document roles. R07 is API-only and should not be assignable from
// the admin screens that manage human users.
export const DOCUMENT_ROLE_IDS = ['R01', 'R02', 'R03', 'R04', 'R05', 'R06'];

// ── Sidebar Visibility ────────────────────────────────────────────────────────
// Values must match the 'category' strings in Sidebar.js menuItems exactly.
// RBAC matrix source: permission spec table (MVP column).
export const SIDEBAR_ACCESS = {
  // R01 — System Admin: full access to everything including Admin & Security
  R01: ['Dashboard', 'Core Modules', 'Integrated Modules', 'System Management'],
  // R02 — National Data Manager: all modules + integrated; no Admin & Security
  R02: ['Dashboard', 'Core Modules', 'Integrated Modules'],
  // R03 — Federal Dept Officer: own dept modules + integrated (read); no Admin
  R03: ['Dashboard', 'Core Modules', 'Integrated Modules'],
  // R04 — State Data Officer: own-state access to all modules; no Admin
  R04: ['Dashboard', 'Core Modules', 'Integrated Modules'],
  // R05 — LGA Field Officer: own-LGA access with simplified field workflow; no Admin.
  R05: ['Dashboard', 'Core Modules'],
  // R06 — External Viewer: dashboard plus all read-only module categories
  R06: ['Dashboard', 'Core Modules', 'Integrated Modules'],
  // R07 — Integration System: no UI at all
  R07: [],
};

// ── Role Permission Matrix ────────────────────────────────────────────────────
// R01 is intentionally all-access. Other roles remain scoped to the actions they
// are expected to perform in the mock UI.
export const ROLE_PERMISSIONS = {
  R01: ['*'],
  R02: [
    'dashboard:read',
    'module:*:read',
    'module:*:create',
    'module:*:update',
    'module:*:validate',
    'module:*:approve',
    'workflow:submit',
    'workflow:validate',
    'workflow:approve',
  ],
  R03: [
    'dashboard:read',
    'module:*:read',
    'module:*:create',
    'module:*:update',
    'module:*:validate',
    'workflow:submit',
    'workflow:validate',
  ],
  R04: [
    'dashboard:read',
    'module:*:read',
    'module:*:create',
    'module:*:update',
    'module:*:validate',
    'workflow:submit',
    'workflow:validate',
  ],
  R05: [
    'dashboard:read',
    'module:population:read',
    'module:population:create',
    'module:markets:read',
    'module:markets:create',
    'module:infrastructure:read',
    'module:infrastructure:create',
    'module:workforce:read',
    'module:workforce:create',
    'module:planning:read',
    'module:conflicts:read',
    'module:conflicts:create',
    'workflow:submit',
  ],
  R06: ['dashboard:read', 'module:*:read'],
  R07: [],
};

export const ROLE_DASHBOARD_BLUEPRINTS = {
  R01: {
    kicker: 'R01 - System Administrator',
    title: 'System Control Tower',
    summary: 'Owns users, institutions, audit logs, reference tables, and integration endpoints.',
    primaryAction: { label: 'Open Admin Console', to: '/admin' },
    secondaryAction: { label: 'Review audit log', to: '/admin/audit' },
    highlights: [
      { label: 'Access plane', value: 'National unrestricted' },
      { label: 'Core duty', value: 'System configuration' },
      { label: 'Fast lane', value: 'Users, audits, integrations' },
    ],
    quickLinks: [
      { label: 'Institution management', to: '/admin/institutions', description: 'Manage the institution registry and structure.' },
      { label: 'User management', to: '/admin/users/list', description: 'Create users and assign role-based access.' },
      { label: 'Audit log', to: '/admin/audit', description: 'Review immutable activity history.' },
      { label: 'Geospatial reference', to: '/admin/geospatial', description: 'Maintain the state, LGA, and ward reference tables.' },
    ],
    workflow: [
      'Register the user or institution in the admin console.',
      'Assign the correct role, scope, and module access.',
      'Monitor audit logs and reference data updates.',
      'Keep integration endpoints and validation tables healthy.',
    ],
  },
  R02: {
    kicker: 'R02 - National Data Manager',
    title: 'National Oversight Dashboard',
    summary: 'Review completeness, cross-module quality, and national approvals across all states.',
    primaryAction: { label: 'Open executive overview', to: '/executive-overview' },
    secondaryAction: { label: 'Open geospatial map', to: '/geospatial-overview' },
    highlights: [
      { label: 'Coverage', value: 'All states' },
      { label: 'Scope', value: 'All modules' },
      { label: 'Role focus', value: 'Consolidate and approve' },
    ],
    quickLinks: [
      { label: 'Executive overview', to: '/executive-overview', description: 'Review national completeness and queue health.' },
      { label: 'Geospatial overview', to: '/geospatial-overview', description: 'See the national spatial picture at a glance.' },
      { label: 'Planning & reporting', to: '/planning-reporting', description: 'Inspect workplans, activity reports, and validation.' },
      { label: 'Animal health', to: '/animal-health', description: 'Track disease surveillance and One Health feeds.' },
    ],
    workflow: [
      'Scan national submission health by module and state.',
      'Validate cross-module gaps and flag incomplete records.',
      'Approve the consolidated national dataset.',
      'Escalate integration disruptions early.',
    ],
  },
  R03: {
    kicker: 'R03 - Federal Department Officer',
    title: 'Department Stewardship Dashboard',
    summary: 'Review national data within assigned modules and move records through validation and approval queues.',
    primaryAction: { label: 'Open executive overview', to: '/executive-overview' },
    secondaryAction: { label: 'Open planning module', to: '/planning-reporting' },
    highlights: [
      { label: 'Coverage', value: 'Assigned modules only' },
      { label: 'Scope', value: 'National, by domain' },
      { label: 'Role focus', value: 'Validate and submit' },
    ],
    quickLinks: [
      { label: 'Planning & reporting', to: '/planning-reporting', description: 'Manage workplans and activity reports.' },
      { label: 'Markets & trade', to: '/markets-trade', description: 'Review registry and price feeds.' },
      { label: 'Animal health', to: '/animal-health', description: 'Monitor outbreak and zoonotic feed status.' },
      { label: 'Workforce & institutional', to: '/workforce-institutional', description: 'Review actors and deployment records.' },
    ],
    workflow: [
      'Open the queue for the assigned modules you oversee.',
      'Validate or reject records with clear notes.',
      'Submit approved records upward to national oversight.',
      'Keep your module dashboards clean and current.',
    ],
  },
  R04: {
    kicker: 'R04 - Supervisor',
    title: 'Supervisor Dashboard',
    summary: 'Review field officer submissions, manage deployments, and keep the state-level picture current.',
    primaryAction: { label: 'Open geospatial overview', to: '/geospatial-overview' },
    secondaryAction: { label: 'Open livestock population', to: '/livestock-population' },
    highlights: [
      { label: 'Coverage', value: 'Own state only' },
      { label: 'Scope', value: 'All modules' },
      { label: 'Role focus', value: 'Pre-validation' },
    ],
    quickLinks: [
      { label: 'Geospatial overview', to: '/geospatial-overview', description: 'Review the state map and facility distribution.' },
      { label: 'Livestock population', to: '/livestock-population', description: 'Inspect population and production submissions.' },
      { label: 'Markets & trade', to: '/markets-trade', description: 'Review market registry and price entries.' },
      { label: 'Conflicts & dispute', to: '/conflicts-dispute', description: 'Handle conflict records and state review.' },
      { label: 'Animal identification', to: '/animal-identification', description: 'Review state-scoped animal registry and movement records.' },
      { label: 'Animal health', to: '/animal-health', description: 'Track state-scoped disease surveillance and vaccination data.' },
    ],
    workflow: [
      'Review LGA drafts inside your state boundary.',
      'Validate, reject, or request corrections with notes.',
      'Submit the state queue to federal domain officers.',
      'Track completeness by LGA and module.',
    ],
  },
  R05: {
    kicker: 'R05 - Field Officer',
    title: 'Field Capture Dashboard',
    summary: 'Use simplified forms, auto-locked geography, and short submission flows for field data capture.',
    primaryAction: { label: 'Start livestock capture', to: '/livestock-population' },
    secondaryAction: { label: 'Open market prices', to: '/markets-trade' },
    highlights: [
      { label: 'Coverage', value: 'Own LGA only' },
      { label: 'Form mode', value: 'Simplified' },
      { label: 'Role focus', value: 'Draft and submit' },
    ],
    quickLinks: [
      { label: 'Livestock population', to: '/livestock-population', description: 'Enter simplified population records.' },
      { label: 'Market prices', to: '/markets-trade', description: 'Capture prices and local market updates.' },
      { label: 'Conflicts & dispute', to: '/conflicts-dispute', description: 'Log field incidents with mandatory fields only.' },
      { label: 'Facility status updates', to: '/infrastructure/status', description: 'Update the functional status of local facilities.' },
    ],
    workflow: [
      'Open the simplified form for the field activity.',
      'Fill only the mandatory fields and auto-locked scope data.',
      'Save as draft if incomplete, or submit once ready.',
      'Let the state officer review your submission next.',
    ],
  },
  R06: {
    kicker: 'R06 - External Viewer',
    title: 'Read-Only Analytics Dashboard',
    summary: 'Inspect approved national aggregates across all modules, with no PII and no write access.',
    primaryAction: { label: 'Open executive overview', to: '/executive-overview' },
    secondaryAction: { label: 'Open geospatial overview', to: '/geospatial-overview' },
    highlights: [
      { label: 'Coverage', value: 'National aggregates' },
      { label: 'Privacy', value: 'No PII' },
      { label: 'Access mode', value: 'Read only' },
    ],
    quickLinks: [
      { label: 'Executive overview', to: '/executive-overview', description: 'Inspect approved national summary panels.' },
      { label: 'Geospatial overview', to: '/geospatial-overview', description: 'See aggregate spatial distribution only.' },
      { label: 'Livestock population', to: '/livestock-population', description: 'Read approved national population summaries.' },
      { label: 'Markets & trade', to: '/markets-trade', description: 'Read approved market and price summaries.' },
      { label: 'Animal health', to: '/animal-health', description: 'Read disease surveillance summaries and flags.' },
      { label: 'Conflicts & dispute', to: '/conflicts-dispute', description: 'Read approved conflict and mediation summaries.' },
    ],
    workflow: [
      'Review approved national summaries only.',
      'Stop at aggregate level and avoid any PII or drill-down.',
      'Export reports only from approved datasets.',
      'Use the dashboard as a read-only reporting surface.',
    ],
  },
};

export function getDashboardBlueprintForRole(roleId) {
  const resolvedRoleId = resolveRoleIdFromValue(roleId) ?? roleId;
  return ROLE_DASHBOARD_BLUEPRINTS[resolvedRoleId] ?? ROLE_DASHBOARD_BLUEPRINTS.R06;
}

// ── Permission Helpers ────────────────────────────────────────────────────────

// Only R01 (System Administrator) may create / edit records in Admin & Security.
// All other roles are view-only on that page.
export const canAdminWrite = (roleId) => resolveRoleIdFromValue(roleId) === 'R01';

export const getRolePermissions = (roleId) => ROLE_PERMISSIONS[resolveRoleIdFromValue(roleId) ?? roleId] ?? [];

// Returns the full role definition for a given roleId, or null if not found.
export const getRole = (roleId) => ROLES[resolveRoleIdFromValue(roleId) ?? roleId] ?? null;

// Returns true if the role has any UI access (false for R07 / API-only systems).
export const hasUIAccess = (roleId) => {
  const resolvedRoleId = resolveRoleIdFromValue(roleId) ?? roleId;
  return Boolean(ROLES[resolvedRoleId] && ROLES[resolvedRoleId].uiAccess !== false);
};

const NATIONAL_SCOPE_ROLES = new Set(['R01', 'R02', 'R03', 'R06']);

// Normalizes the geographic scope that the UI should honor for a given role.
// National roles must never inherit a backend state/LGA value, because that
// would accidentally turn national dashboards into local-only views.
export function normalizeRoleScope(roleId, scope = {}) {
  const resolvedRoleId = resolveRoleIdFromValue(roleId);
  const rawLevel = String(scope?.level ?? '').trim().toLowerCase();
  const state = scope?.state ?? null;
  const lga = scope?.lga ?? null;
  const ward = scope?.ward ?? null;

  if (NATIONAL_SCOPE_ROLES.has(resolvedRoleId)) {
    return { level: 'national', state: null, lga: null, ward: null };
  }

  if (resolvedRoleId === 'R04') {
    return { level: 'state', state, lga: null, ward: null };
  }

  if (resolvedRoleId === 'R05') {
    return { level: 'lga', state, lga, ward };
  }

  return {
    level: rawLevel || (lga ? 'lga' : state ? 'state' : 'national'),
    state,
    lga,
    ward,
  };
}

export const MODULE_KEYS = {
  DASHBOARD: 'dashboard',
  POPULATION: 'population',
  MARKETS: 'markets',
  INFRASTRUCTURE: 'infrastructure',
  WORKFORCE: 'workforce',
  PLANNING: 'planning',
  CONFLICTS: 'conflicts',
  ANIMAL_IDENTIFICATION: 'animalIdentification',
  ANIMAL_HEALTH: 'animalHealth',
  PPP: 'ppp',
  ADMIN: 'admin',
};

const FIELD_MODULE_KEYS = [
  MODULE_KEYS.POPULATION,
  MODULE_KEYS.MARKETS,
  MODULE_KEYS.INFRASTRUCTURE,
  MODULE_KEYS.WORKFORCE,
  MODULE_KEYS.PLANNING,
  MODULE_KEYS.CONFLICTS,
];

const MODULE_SCOPE_MAP = {
  mod_1: MODULE_KEYS.POPULATION,
  mod_2: MODULE_KEYS.MARKETS,
  mod_3: MODULE_KEYS.INFRASTRUCTURE,
  mod_4: MODULE_KEYS.WORKFORCE,
  mod_5: MODULE_KEYS.PLANNING,
  mod_6: MODULE_KEYS.CONFLICTS,
  mod_7: MODULE_KEYS.ANIMAL_IDENTIFICATION,
  mod_8: MODULE_KEYS.ANIMAL_HEALTH,
};

function normalizeModuleKey(value) {
  const text = String(value ?? '').trim();
  if (!text) return null;
  if (Object.values(MODULE_KEYS).includes(text)) return text;
  if (MODULE_SCOPE_MAP[text]) return MODULE_SCOPE_MAP[text];
  if (MODULE_SCOPE_MAP[text.toLowerCase()]) return MODULE_SCOPE_MAP[text.toLowerCase()];

  const compact = text.toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (!compact) return null;

  if (
    compact === '1' ||
    compact === 'mod1' ||
    compact === 'module1' ||
    compact.includes('livestockpopulation') ||
    compact.includes('populationrecords') ||
    compact === 'population'
  ) {
    return MODULE_KEYS.POPULATION;
  }

  if (
    compact === '2' ||
    compact === 'mod2' ||
    compact === 'module2' ||
    compact.includes('marketstrade') ||
    compact === 'markets'
  ) {
    return MODULE_KEYS.MARKETS;
  }

  if (
    compact === '3' ||
    compact === 'mod3' ||
    compact === 'module3' ||
    compact.includes('infrastructure') ||
    compact.includes('services')
  ) {
    return MODULE_KEYS.INFRASTRUCTURE;
  }

  if (
    compact === '4' ||
    compact === 'mod4' ||
    compact === 'module4' ||
    compact.includes('workforce') ||
    compact.includes('institutional') ||
    compact.includes('actor')
  ) {
    return MODULE_KEYS.WORKFORCE;
  }

  if (
    compact === '5' ||
    compact === 'mod5' ||
    compact === 'module5' ||
    compact.includes('planning')
  ) {
    return MODULE_KEYS.PLANNING;
  }

  if (
    compact === '6' ||
    compact === 'mod6' ||
    compact === 'module6' ||
    compact.includes('conflict') ||
    compact.includes('dispute')
  ) {
    return MODULE_KEYS.CONFLICTS;
  }

  if (
    compact === '7' ||
    compact === 'mod7' ||
    compact === 'module7' ||
    compact.includes('animalidentification')
  ) {
    return MODULE_KEYS.ANIMAL_IDENTIFICATION;
  }

  if (
    compact === '8' ||
    compact === 'mod8' ||
    compact === 'module8' ||
    compact.includes('animalhealth')
  ) {
    return MODULE_KEYS.ANIMAL_HEALTH;
  }

  return null;
}

function getAssignedModuleSet(user = {}) {
  const tokens = [];

  const pushTokens = (value) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach((entry) => pushTokens(entry));
      return;
    }
    if (typeof value === 'object') {
      pushTokens(
        value.module_scope ??
        value.moduleScope ??
        value.module ??
        value.moduleKey ??
        value.module_key ??
        value.module_code ??
        value.moduleCode ??
        value.module_id ??
        value.moduleId ??
        value.code ??
        value.slug ??
        value.name ??
        value.label
      );
      return;
    }

    String(value)
      .split(/[,\s|;]+/)
      .forEach((token) => {
        if (token) tokens.push(token);
      });
  };

  pushTokens(user.assignedModules);
  pushTokens(user.assigned_modules);
  pushTokens(user.module_scope);
  pushTokens(user.moduleScope);
  pushTokens(user.module_assignments);
  pushTokens(user.moduleAssignments);
  pushTokens(user.role_assignment);
  pushTokens(user.roleAssignment);
  pushTokens(user.role_assignments);
  pushTokens(user.roleAssignments);
  pushTokens(user.assignment);
  pushTokens(user.assignments);

  if (Array.isArray(user.roles)) {
    user.roles.forEach((role) => {
      pushTokens(role?.module_scope);
      pushTokens(role?.moduleScope);
      pushTokens(role?.module_assignments);
      pushTokens(role?.moduleAssignments);
      pushTokens(role?.role_assignment);
      pushTokens(role?.roleAssignment);
      pushTokens(role?.assignments);
    });
  }

  return new Set(tokens.map(normalizeModuleKey).filter(Boolean));
}

export function getLandingRouteForRole(userOrRoleId) {
  const user = typeof userOrRoleId === 'object' && userOrRoleId !== null ? userOrRoleId : null;
  const roleId = typeof userOrRoleId === 'string' ? userOrRoleId : user?.roleId ?? null;
  switch (roleId) {
    case 'R01':
      return '/admin';
    case 'R02':
    case 'R03':
    case 'R04':
    case 'R05':
    case 'R06':
      return '/dashboard';
    case 'R07':
      return '/login';
    default:
      return '/dashboard';
  }
}

export const ROUTE_MODULES = {
  '/dashboard': MODULE_KEYS.DASHBOARD,
  '/executive-overview': MODULE_KEYS.DASHBOARD,
  '/geospatial-overview': MODULE_KEYS.DASHBOARD,
  '/livestock-population': MODULE_KEYS.POPULATION,
  '/markets-trade': MODULE_KEYS.MARKETS,
  '/infrastructure': MODULE_KEYS.INFRASTRUCTURE,
  '/workforce-institutional': MODULE_KEYS.WORKFORCE,
  '/planning-reporting': MODULE_KEYS.PLANNING,
  '/conflicts-dispute': MODULE_KEYS.CONFLICTS,
  '/ppp-arrangements': MODULE_KEYS.PPP,
  '/animal-identification': MODULE_KEYS.ANIMAL_IDENTIFICATION,
  '/animal-health': MODULE_KEYS.ANIMAL_HEALTH,
  '/admin': MODULE_KEYS.ADMIN,
  '/registry-management': MODULE_KEYS.ADMIN,
};

export function hasPermission(permissions = [], required) {
  if (!required) return true;
  if (permissions.includes('*')) return true;
  if (permissions.includes(required)) return true;

  const [scope, moduleKey, action] = required.split(':');
  return permissions.some((permission) => {
    const [pScope, pModule, pAction] = permission.split(':');
    return (
      (pScope === scope || pScope === '*') &&
      (pModule === moduleKey || pModule === '*') &&
      (pAction === action || pAction === '*')
    );
  });
}

export function canReadModule(permissions, moduleKey) {
  if (moduleKey === MODULE_KEYS.DASHBOARD) return hasPermission(permissions, 'dashboard:read');
  if (moduleKey === MODULE_KEYS.ADMIN) return hasPermission(permissions, '*');
  return hasPermission(permissions, `module:${moduleKey}:read`);
}

export function canCreateModule(permissions, moduleKey) {
  return hasPermission(permissions, `module:${moduleKey}:create`);
}

export function canAccessModule(user, permissions, moduleKey) {
  if (!hasUIAccess(user?.roleId)) return false;
  if (moduleKey === MODULE_KEYS.DASHBOARD) return canReadModule(permissions, moduleKey);
  if (moduleKey === MODULE_KEYS.ADMIN) return user?.roleId === 'R01';

  const assignedModules = getAssignedModuleSet(user);

  switch (user?.roleId) {
    case 'R01':
      return true;
    case 'R02':
      return moduleKey !== MODULE_KEYS.ADMIN;
    case 'R03':
      return assignedModules.size > 0
        ? assignedModules.has(moduleKey)
        : moduleKey !== MODULE_KEYS.ADMIN;
    case 'R04':
      return moduleKey !== MODULE_KEYS.ADMIN;
    case 'R05':
      return FIELD_MODULE_KEYS.includes(moduleKey);
    case 'R06':
      return canReadModule(permissions, moduleKey);
    default:
      return canReadModule(permissions, moduleKey);
  }
}

export function getWorkflowActions(permissions = [], status = 'Draft') {
  const normalized = String(status).toLowerCase();
  const actions = [
    { key: 'submit', label: 'Submit', from: ['draft', 'rejected'], permission: 'workflow:submit' },
    { key: 'validate', label: 'Validate', from: ['submitted'], permission: 'workflow:validate' },
    { key: 'approve', label: 'Approve', from: ['validated'], permission: 'workflow:approve' },
    { key: 'reject', label: 'Reject', from: ['submitted', 'validated'], permission: 'workflow:validate' },
  ];

  return actions.filter((action) =>
    action.from.includes(normalized) && hasPermission(permissions, action.permission)
  );
}
